import React, { useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';
import { Drawer } from 'components/layouts';

const useStyles = Mui.makeStyles(theme => ({
  sectionTitle: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.08em',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    textTransform: 'uppercase',
  },
  columnRow: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },
  columnRowDragging: { opacity: 0.5 },
  columnRowDragOver: { border: `1px dashed ${theme.palette.primary.main}` },
  dragHandle: {
    color: theme.palette.text.secondary,
    cursor: 'grab',
    marginRight: theme.spacing(1),
  },
  columnLabel: { alignItems: 'center', display: 'flex', flex: 1, minWidth: 0 },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 3),
  },
}));

const buildDefaultVisibility = columns =>
  Object.fromEntries(columns.map(column => [column.id, column.defaultVisible !== false]));

function ColumnCustomizer({ open, onClose, columns, visibility, order, onSave }) {
  // Hooks
  const classes = useStyles();

  // States
  const [draft, setDraft] = useState(() => ({
    visibility,
    order: order && order.length ? order : columns.map(column => column.id),
  }));
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Constants
  const orderedColumns = useMemo(() => {
    const position = id => {
      const index = draft.order.indexOf(id);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };

    return [...columns].sort((columnA, columnB) => position(columnA.id) - position(columnB.id));
  }, [columns, draft.order]);

  const declaredOrder = columns.map(column => column.id);
  const defaultVisibility = buildDefaultVisibility(columns);
  const isVisible = column => draft.visibility[column.id] !== false;

  const hasChanges =
    draft.order.join() !== (order.length ? order : declaredOrder).join() ||
    columns.some(column => isVisible(column) !== (visibility[column.id] !== false));

  const canReset =
    draft.order.join() !== declaredOrder.join() ||
    columns.some(column => isVisible(column) !== defaultVisibility[column.id]);

  // Actions
  const handleToggle = columnId =>
    setDraft(prevDraft => ({
      ...prevDraft,
      visibility: { ...prevDraft.visibility, [columnId]: prevDraft.visibility[columnId] === false },
    }));

  const handleReset = () => setDraft({ visibility: defaultVisibility, order: declaredOrder });

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  const handleDrop = (event, targetId) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData('text/plain') || draggedId;

    setDraggedId(null);
    setDragOverId(null);

    if (!sourceId || !targetId || sourceId === targetId) return;

    setDraft(prevDraft => {
      const nextOrder = orderedColumns.map(column => column.id);
      const sourceIndex = nextOrder.indexOf(sourceId);
      const targetIndex = nextOrder.indexOf(targetId);

      nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, sourceId);

      return { ...prevDraft, order: nextOrder };
    });
  };

  const getDragProps = column => {
    if (column.pinned) return {};

    return {
      draggable: true,
      onDragStart: event => {
        const transfer = event.dataTransfer;

        setDraggedId(column.id);
        transfer.effectAllowed = 'move';
        transfer.setData('text/plain', column.id);
      },
      onDragOver: event => {
        const transfer = event.dataTransfer;

        event.preventDefault();
        transfer.dropEffect = 'move';
      },
      onDragEnter: event => {
        event.preventDefault();
        setDragOverId(column.id);
      },
      onDrop: event => handleDrop(event, column.id),
      onDragEnd: () => {
        setDraggedId(null);
        setDragOverId(null);
      },
    };
  };

  // Effects
  useEffect(() => {
    if (open)
      setDraft({ visibility, order: order.length ? order : columns.map(column => column.id) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Render
  const renderSection = (title, sectionColumns) => {
    if (!sectionColumns.length) return null;

    return (
      <>
        <Mui.Typography className={classes.sectionTitle}>{title}</Mui.Typography>
        {sectionColumns.map(column => (
          <Mui.Box
            key={column.id}
            className={clsx(
              classes.columnRow,
              draggedId === column.id && classes.columnRowDragging,
              dragOverId === column.id && draggedId !== column.id && classes.columnRowDragOver,
            )}
            {...getDragProps(column)}>
            <Mui.Box className={classes.columnLabel}>
              {!column.pinned && (
                <Mui.Icon className={classes.dragHandle} fontSize="small">
                  drag_indicator
                </Mui.Icon>
              )}
              <Mui.Typography variant="body2">{column.label}</Mui.Typography>
            </Mui.Box>
            <Mui.Switch
              checked={isVisible(column)}
              color="primary"
              disabled={column.pinned}
              onChange={() => handleToggle(column.id)}
            />
          </Mui.Box>
        ))}
      </>
    );
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} title="Personalizar colunas" width={420}>
      <Mui.Box px={3} pb={2}>
        {renderSection(
          'Colunas principais',
          orderedColumns.filter(column => column.group !== 'additional'),
        )}
        {renderSection(
          'Colunas adicionais',
          orderedColumns.filter(column => column.group === 'additional'),
        )}
      </Mui.Box>
      <Mui.Box className={classes.footer}>
        <Mui.Button color="secondary" disabled={!canReset} onClick={handleReset} variant="outlined">
          Redefinir
        </Mui.Button>
        <Mui.Button color="primary" disabled={!hasChanges} onClick={handleSave} variant="contained">
          Salvar
        </Mui.Button>
      </Mui.Box>
    </Drawer>
  );
}

ColumnCustomizer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  visibility: PropTypes.object.isRequired,
  order: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ColumnCustomizer;
